import React, { ReactElement, useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import ActionMenu from '@components/RingilComponents/ActionMenu.tsx';
import SvgDots from '@icons/3-dots-horizontal-inverted.svg?react';
import { tabOverflowLayout } from './tabOverflowLayout';

export interface TabProps {
  label: string;
  active: boolean;
  icon?: ReactElement<{ color?: string }>;
  /** Section the tab scrolls to. Without one the tab only reports the click — a pane switch, or a trigger like Messages. */
  anchorId?: string;
  dataTestId?: string;
}

/** Matches the strip's `gap-2`. */
const TAB_GAP = 8;

/** Frames the page height must hold steady before the lazy sections count as loaded. */
const LAYOUT_STABLE_FRAMES = 10;
/** Ceiling on that wait, so a page that never stops changing still gets its one follow-up scroll. */
const FOLLOW_UP_MAX_MS = 10000;
/** Sub-pixel rounding must not read as "the page is too short" or "the section moved". */
const ALIGN_TOLERANCE_PX = 2;
const SCROLL_KEYS = new Set(['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' ']);
/** A section counts as current once its top reaches the underside of the sticky strip (plus slack for rounding). */
const ACTIVE_LINE_SLACK = 8;
/** Below this the view cannot meaningfully scroll, so scroll position carries no signal. */
const SCROLLABLE_MIN = 8;

/** Nearest ancestor that actually scrolls, or null when the page/window is the scroller. */
const findScroller = (el: HTMLElement | null): HTMLElement | null => {
  for (let p = el?.parentElement; p; p = p.parentElement) {
    const overflowY = getComputedStyle(p).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return p;
  }
  return null;
};

interface TabsProps {
  tabs: TabProps[];
  onTabChange?: (activeTabIndex: number) => void;
  replaceUrl?: boolean;
  /**
   * Keep empty space after the last section so every section can be scrolled up to the strip. Worth turning off
   * where the sections are short and of a kind — a list of orders in a sidebar — since there the trailing gap is
   * more noticeable than the last order not quite reaching the top.
   */
  tailSpacer?: boolean;
  /** Extra classes for the strip, e.g. a tighter margin where the content below brings its own spacing. */
  className?: string;
}

/**
 * Sticky strip over a single scrolling view: clicking a tab scrolls its section under the strip, and scrolling
 * highlights whichever section you are in.
 *
 * Two rules keep the two directions from fighting, and everything below is just those rules:
 *  - a click owns the selection until the user scrolls by hand (wheel, touch, keyboard — never a position
 *    comparison, which the page's own settling keeps tripping), so a section that cannot reach the top of the
 *    view still stays selected;
 *  - only the real scroll container is ever scrolled, never `scrollIntoView`, which also shifts every
 *    `overflow: hidden` wrapper above it and leaves the shell stuck with dead space and no scrollbar.
 */
const Tabs: React.FC<TabsProps> = ({ tabs, onTabChange, replaceUrl = false, tailSpacer = true, className }) => {
  const { tr } = useTranslationLgs();
  const barRef = useRef<HTMLDivElement>(null);
  // Hidden copy of every tab (and of the "…" button) at its natural width, so the strip can show as many as fit
  // and fold the rest — including tabs folded right now, which can't be measured in place.
  const measureRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<{ widths: number[]; more: number; available: number } | null>(null);
  const labelKey = tabs.map(tab => tab.label).join('|');

  useLayoutEffect(() => {
    const bar = barRef.current;
    const measure = measureRef.current;
    if (!bar || !measure) return;
    const update = () => {
      const children = Array.from(measure.children) as HTMLElement[];
      const more = children.pop()?.offsetWidth ?? 0;
      const next = { widths: children.map(child => child.offsetWidth), more, available: bar.clientWidth };
      setMeasured(current =>
        current && current.more === next.more && current.available === next.available && current.widths.join() === next.widths.join() ? current : next,
      );
    };
    update();
    // The copy too: its widths change with the labels' font loading in and the mobile breakpoint hiding them.
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [labelKey]);
  // Callers rebuild `tabs` (and often `onTabChange`) on every render, so nothing below may depend on their
  // identity — otherwise the listeners and observers are torn down and re-created on each render, which is
  // half of why the strip behaved erratically. Only the anchor ids matter, and those are compared by value.
  const tabsDataRef = useRef(tabs);
  tabsDataRef.current = tabs;
  const onTabChangeRef = useRef(onTabChange);
  onTabChangeRef.current = onTabChange;
  const tailSpacerRef = useRef(tailSpacer);
  tailSpacerRef.current = tailSpacer;
  const anchorKey = tabs.map(tab => tab.anchorId ?? '').join('|');

  const scrollerRef = useRef<HTMLElement | null>(null);
  const activeIndexRef = useRef<number | null>(null);
  const syncFrameRef = useRef<number | null>(null);
  // A click's selection outranks scroll position until the user scrolls. Scroll position alone cannot decide
  // between sections sharing the screen — click the second of two short trailing sections and both are visible
  // at once, with the last one winning — so the explicit choice has to hold.
  const clickOwnsSelectionRef = useRef(false);
  // Pending re-check for a section the page was too short to reach when it was clicked.
  const followUpFrameRef = useRef<number | null>(null);
  // Empty block kept after the content so the trailing sections can be scrolled up to the strip.
  const spacerRef = useRef<HTMLDivElement | null>(null);

  const updateActiveTab = useCallback(
    // Always fires, even for the already-active index: a click on a tab that acts as a trigger (Messages opens a
    // dialog rather than selecting a section) must keep working after the dialog is closed.
    (index: number) => {
      activeIndexRef.current = index;
      onTabChangeRef.current?.(index);

      if (replaceUrl) {
        const params = new URLSearchParams(window.location.search);
        const anchor = tabsDataRef.current[index]?.anchorId;
        params.set('tab', anchor ?? String(index));
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
      }
    },
    [replaceUrl],
  );

  /** Distance from the top of the scrollport at which a section is considered "aligned" — under the strip. */
  const alignedTop = useCallback(() => barRef.current?.offsetHeight ?? 0, []);

  /**
   * The scroll position that puts a section under the sticky strip, clamped to what the container can actually
   * reach. Read fresh each time: while lazy sections are still mounting, the reachable maximum grows, so the
   * same section has a different answer a second later.
   *
   * `short` says the page cannot currently scroll that far — the sections below have not loaded yet, so there
   * is not enough content underneath. That is the one case worth revisiting once they arrive.
   */
  const desiredTop = useCallback(
    (target: HTMLElement) => {
      const scroller = scrollerRef.current;
      if (!scroller) return { top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - alignedTop()), short: false };

      const offset = Math.max(0, target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - alignedTop());
      const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      return { top: Math.min(offset, maxScroll), short: offset > maxScroll + ALIGN_TOLERANCE_PX };
    },
    [alignedTop],
  );

  /**
   * Scroll only the real scroll container — never `target.scrollIntoView()`, which adjusts `scrollTop` on every
   * scrollable ancestor. The shell wrappers (`main`, the flex row, the `h-screen` box) are `overflow: hidden`:
   * programmatically scrollable, but not user-scrollable. When the inner scroller alone cannot satisfy the
   * request the browser takes the difference out of one of those, sliding the whole shell up — content clipped,
   * dead space below, and the wheel cannot undo it because the wheel drives the inner container.
   *
   * The animation is asked for here rather than left to a `scroll-behavior` on the container, so the strip
   * behaves the same wherever it is used — the page shell, an admin page, a sidebar — without each host having
   * to style its own scrollport. Safe because scrolls are never issued on top of one another: the only second
   * one waits for the page to have stopped changing, long after the first has arrived.
   */
  const scrollTop = useCallback((top: number) => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTo({ top, behavior: 'smooth' });
    else window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  /**
   * Undo a stray scroll of an `overflow: hidden` wrapper above the scroller. Nothing here scrolls them, but
   * focus moves and portaled overlays still can, and the user has no way back out of it — a hidden box has no
   * scrollbar. Ancestors that genuinely scroll are left alone.
   */
  const resetAncestorScroll = useCallback(() => {
    for (let p = (scrollerRef.current ?? barRef.current)?.parentElement; p; p = p.parentElement) {
      if (p.scrollTop === 0 && p.scrollLeft === 0) continue;
      const overflowY = getComputedStyle(p).overflowY;
      if (overflowY !== 'hidden' && overflowY !== 'clip') continue;
      p.scrollTop = 0;
      p.scrollLeft = 0;
    }
  }, []);

  const anchoredSections = useCallback(
    () =>
      tabsDataRef.current
        .map((tab, index) => ({ index, el: tab.anchorId ? document.getElementById(tab.anchorId) : null }))
        .filter((s): s is { index: number; el: HTMLElement } => !!s.el),
    [],
  );

  /**
   * Keep enough empty space after the last section that any section can be scrolled up to the strip.
   *
   * Without it the trailing sections are unreachable whenever they add up to less than a screenful: the scroll
   * runs out first and they can only ever sit low in the view. The spacer is measured, not fixed — exactly the
   * shortfall and nothing more — so it shrinks to zero as the content below grows, and pages whose last
   * section is already tall enough get no space at all.
   */
  const syncTailSpacer = useCallback(() => {
    if (!tailSpacerRef.current) {
      spacerRef.current?.remove();
      spacerRef.current = null;
      return;
    }

    const scroller = scrollerRef.current;
    const sections = anchoredSections();
    if (!scroller || sections.length === 0) return;

    let spacer = spacerRef.current;
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.dataset.tabsTailSpacer = '';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.style.flexShrink = '0';
      spacer.style.pointerEvents = 'none';
      spacerRef.current = spacer;
    }
    // Always last: sections mount lazily, and React appends them after whatever is already there.
    if (spacer.parentElement !== scroller || spacer.nextSibling) scroller.appendChild(spacer);

    const last = sections[sections.length - 1].el;
    const lastTopInContent = last.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    // Measured without our own contribution, otherwise the spacer feeds on itself.
    const tail = Math.max(0, scroller.scrollHeight - spacer.offsetHeight - lastTopInContent);
    const needed = Math.max(0, scroller.clientHeight - alignedTop() - tail);
    if (Math.abs(needed - spacer.offsetHeight) > 1) spacer.style.height = `${needed}px`;
  }, [alignedTop, anchoredSections]);

  /** Derive the active tab from where the view actually is. */
  const syncActiveFromScroll = useCallback(() => {
    if (clickOwnsSelectionRef.current) return;

    const sections = anchoredSections();
    if (sections.length === 0) return;

    const scroller = scrollerRef.current;
    const viewportHeight = scroller ? scroller.clientHeight : window.innerHeight;
    const scrollHeight = scroller ? scroller.scrollHeight : document.documentElement.scrollHeight;
    // Nothing to scroll (a tall screen showing every section at once) — scroll position carries no signal, so
    // leave whatever is selected instead of snapping back to the first tab.
    if (scrollHeight - viewportHeight < SCROLLABLE_MIN) return;

    // A section becomes current when its top crosses a horizontal "reading line". At rest that line sits just
    // under the sticky strip, but sections near the end of the page can never reach it — the scroll runs out
    // first, and there is deliberately no filler after the last section to make them reachable. So over the
    // final screenful (or the whole range, when the content barely overflows) the line sweeps down to the
    // bottom of the view, letting each trailing section take its turn. A fixed line plus a "snap to the last
    // one at the bottom" rule instead skips whatever sits in between.
    const scrollerRect = scroller?.getBoundingClientRect();
    const viewportBottom = scrollerRect ? scrollerRect.bottom : window.innerHeight;
    const lineAtRest = (barRef.current?.getBoundingClientRect().bottom ?? scrollerRect?.top ?? 0) + ACTIVE_LINE_SLACK;

    const currentTop = scroller ? scroller.scrollTop : window.scrollY;
    const maxScroll = scrollHeight - viewportHeight;
    const sweepRange = Math.min(viewportHeight, maxScroll);
    const sweep = sweepRange > 0 ? Math.min(1, Math.max(0, 1 - (maxScroll - currentTop) / sweepRange)) : 1;
    const line = lineAtRest + sweep * Math.max(0, viewportBottom - lineAtRest);

    let next = sections[0].index;
    sections.forEach(({ index, el }) => {
      if (el.getBoundingClientRect().top <= line) next = index;
    });

    if (next !== activeIndexRef.current) updateActiveTab(next);
  }, [updateActiveTab, anchoredSections]);

  const scheduleSync = useCallback(() => {
    if (syncFrameRef.current !== null) return;
    syncFrameRef.current = requestAnimationFrame(() => {
      syncFrameRef.current = null;
      resetAncestorScroll();
      syncTailSpacer();
      syncActiveFromScroll();
    });
  }, [syncActiveFromScroll, resetAncestorScroll, syncTailSpacer]);

  const cancelFollowUp = useCallback(() => {
    if (followUpFrameRef.current !== null) {
      cancelAnimationFrame(followUpFrameRef.current);
      followUpFrameRef.current = null;
    }
  }, []);

  /** The user took over: their scrolling decides the selection from here, and nothing else may move the view. */
  const releaseToUser = useCallback(() => {
    clickOwnsSelectionRef.current = false;
    cancelFollowUp();
    scheduleSync();
  }, [scheduleSync, cancelFollowUp]);

  /**
   * The one follow-up, for the one case that needs it: the section could not be reached because the sections
   * below it had not loaded yet.
   *
   * Nothing moves while they load — chasing each piece as it arrives is what turned a click into a scroll in
   * several stages. It waits for the page height to hold steady, then scrolls once more, and that is the end
   * of it. A section that was already reachable never gets here, so the common case stays a single scroll.
   */
  const scheduleFollowUp = useCallback(
    (anchorId: string) => {
      cancelFollowUp();
      const until = performance.now() + FOLLOW_UP_MAX_MS;
      let lastHeight = -1;
      let stableFrames = 0;

      const tick = () => {
        followUpFrameRef.current = null;
        const target = document.getElementById(anchorId);
        const scroller = scrollerRef.current;
        if (!target || !scroller) return;

        if (scroller.scrollHeight === lastHeight) stableFrames += 1;
        else {
          lastHeight = scroller.scrollHeight;
          stableFrames = 0;
        }

        if (stableFrames < LAYOUT_STABLE_FRAMES && performance.now() < until) {
          followUpFrameRef.current = requestAnimationFrame(tick);
          return;
        }
        // Loaded. Move only if the section is not already where it should be.
        syncTailSpacer();
        const { top } = desiredTop(target);
        if (Math.abs(top - scroller.scrollTop) > ALIGN_TOLERANCE_PX) scrollTop(top);
      };
      followUpFrameRef.current = requestAnimationFrame(tick);
    },
    [cancelFollowUp, desiredTop, scrollTop, syncTailSpacer],
  );

  /**
   * Scroll a section under the strip — as far as the page allows right now.
   *
   * When it does not allow far enough (the content below is still loading) the scroll clamps and the section
   * lands low in the view; that is when, and only when, a follow-up is queued for once the loading is done.
   */
  const goToAnchor = useCallback(
    (anchorId: string, target: HTMLElement) => {
      clickOwnsSelectionRef.current = true;
      cancelFollowUp();

      // Size the tail first, so the section is reachable before we work out where to send the view.
      syncTailSpacer();
      const { top, short } = desiredTop(target);
      scrollTop(top);
      if (short) scheduleFollowUp(anchorId);
    },
    [desiredTop, scrollTop, scheduleFollowUp, cancelFollowUp, syncTailSpacer],
  );

  const handleTabClick = useCallback(
    (index: number, anchorId?: string) => {
      // Select immediately. Waiting for a scroll animation to finish first read as a dead click — and for the
      // Messages tab (a trigger, not a section) it delayed opening the dialog by that whole time.
      updateActiveTab(index);

      const target = anchorId ? document.getElementById(anchorId) : null;
      if (!target || !anchorId) {
        releaseToUser();
        return;
      }
      goToAnchor(anchorId, target);
    },
    [updateActiveTab, goToAnchor, releaseToUser],
  );

  // A gesture hands control back immediately. `scroll` alone cannot detect this — our own scrolling fires it
  // too — so gestures are read directly, and the scroll handler below covers the scrollbar and anything else.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) releaseToUser();
    };
    window.addEventListener('wheel', releaseToUser, { passive: true });
    window.addEventListener('touchmove', releaseToUser, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('wheel', releaseToUser);
      window.removeEventListener('touchmove', releaseToUser);
      window.removeEventListener('keydown', onKeyDown);
      cancelFollowUp();
    };
  }, [releaseToUser, cancelFollowUp]);

  // Scroll-spy. Capture phase, because scroll events from the inner scroll container do not bubble.
  useEffect(() => {
    const onScroll = () => {
      // Deliberately no "the position is not where we left it, so the user must have scrolled" rule here. The
      // page keeps settling for a while after a click — late content, the browser's own scroll anchoring — and
      // every one of those nudges looked like the user taking over, which handed the highlight to whichever
      // section the position then pointed at. Only a real gesture releases the click's selection.
      scheduleSync();
    };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      if (syncFrameRef.current !== null) {
        cancelAnimationFrame(syncFrameRef.current);
        syncFrameRef.current = null;
      }
    };
  }, [scheduleSync, releaseToUser]);

  // The tail spacer lives in the scroll container, not in this subtree, so it has to be taken out by hand.
  useEffect(
    () => () => {
      spacerRef.current?.remove();
      spacerRef.current = null;
    },
    [],
  );

  // Sections mount lazily, so re-derive the scroller whenever the set of anchors changes rather than waiting
  // for the next scroll.
  useEffect(() => {
    const first = tabsDataRef.current.map(tab => (tab.anchorId ? document.getElementById(tab.anchorId) : null)).find(Boolean) ?? null;
    scrollerRef.current = findScroller(first ?? barRef.current);
    scheduleSync();
  }, [anchorKey, scheduleSync]);

  // Only honor ?tab= on load when this Tabs owns the URL (replaceUrl). Otherwise a stale param (e.g. left by
  // another page or a previous replaceUrl session) would scroll us away from the top. Once per mount —
  // re-running it on every render fought whatever the user did afterwards.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !replaceUrl) return;
    const currentTabs = tabsDataRef.current;
    const tabParam = new URLSearchParams(window.location.search).get('tab');
    if (!tabParam || currentTabs.length === 0) return;
    restoredRef.current = true;

    let initialIndex = currentTabs.findIndex(t => t.anchorId === tabParam);
    if (initialIndex === -1) {
      const idx = Number.parseInt(tabParam, 10);
      if (!Number.isNaN(idx) && idx >= 0 && idx < currentTabs.length) initialIndex = idx;
    }
    if (initialIndex === -1) return;

    updateActiveTab(initialIndex); // neupdatuj URL znovu
    const anchorId = currentTabs[initialIndex].anchorId;
    const target = anchorId ? document.getElementById(anchorId) : null;
    if (target && anchorId) goToAnchor(anchorId, target);
  }, [anchorKey, replaceUrl, updateActiveTab, goToAnchor]);

  // Laid out again on every render from the cached widths, so when scroll-spy (or a click) opens a folded tab it
  // comes out of the menu straight away and its neighbours fold to make room — nothing is measured mid-scroll.
  const activeIndex = tabs.findIndex(tab => tab.active);
  const { visible, folded } = measured
    ? tabOverflowLayout(measured.widths, measured.available, TAB_GAP, measured.more, activeIndex)
    : { visible: tabs.map((_, i) => i), folded: [] as number[] };

  const renderTab = (tab: TabProps, onClick?: () => void, dataTestId?: string) => (
    <div
      onClick={onClick}
      data-testid={dataTestId}
      className={clsx(
        'flex shrink-0 py-4 cursor-pointer uppercase font-semibold relative',
        tab.active &&
          'border-b-2 border-content-brand text-content-brand [&:after]:content-[""] [&:after]:h-[3px] [&:after]:w-full [&:after]:bg-content-brand [&:after]:absolute [&:after]:bottom-0',
        tab.active ? 'bg-white' : 'bg-[var(--ringil3-quaternary-color)]',
      )}
    >
      <div className="flex px-6 gap-4 whitespace-nowrap justify-center items-center">
        {tab.icon && <IconWrapper $active={tab.active}>{tab.icon}</IconWrapper>}
        <div className={clsx('flex text-sm tracking-wider max-sm:hidden', tab.active ? 'text-content-brand' : 'text-content-gr4')}>{tab.label}</div>
      </div>
    </div>
  );

  const moreMenu = (indices: number[], dataTestId?: string) => (
    <ActionMenu
      items={indices.map(index => ({
        key: `tab-${index}`,
        label: tabs[index].label,
        icon: tabs[index].icon,
        onClick: () => handleTabClick(index, tabs[index].anchorId),
        dataTestId: tabs[index].dataTestId,
      }))}
      icon={<SvgDots className="w-4 h-4" />}
      mode="text-only"
      density="compact"
      tooltip={tr('generic.moreActions', 'More actions')}
      triggerClassName="h-auto self-stretch px-4 rounded-none bg-[var(--ringil3-quaternary-color)] text-content-gr4 enabled:hover:text-content-brand"
      dataTestId={dataTestId}
    />
  );

  return (
    <div
      ref={barRef}
      // overflow-hidden clips the measuring copy, which would otherwise widen the pane's scroll area.
      className={twMerge(
        'flex min-w-0 overflow-hidden mt-6 mb-8 gap-2 border-solid border-0 border-b-2 border-content-brand sticky top-0 z-[var(--z-tabs)] bg-base-tertiary',
        className,
      )}
    >
      {visible.map(index => (
        <React.Fragment key={index}>{renderTab(tabs[index], () => handleTabClick(index, tabs[index].anchorId), tabs[index].dataTestId)}</React.Fragment>
      ))}
      {folded.length > 0 && moreMenu(folded, 'ringilTabs-more')}

      {/* Out of the flow and out of sight; no test ids, so tests only ever find the real tabs. The last child is the "…" button. */}
      <div ref={measureRef} aria-hidden className="pointer-events-none invisible absolute left-0 top-0 flex w-max gap-2">
        {tabs.map((tab, index) => (
          <React.Fragment key={index}>{renderTab(tab)}</React.Fragment>
        ))}
        {tabs.length > 0 && moreMenu([0])}
      </div>
    </div>
  );
};

const IconWrapper = styled.div<{ $active?: boolean }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    color: ${props => (props.$active ? 'var(--ringil3-brand-color)' : 'inherit')};
    fill: ${props => (props.$active ? 'var(--ringil3-brand-color)' : 'currentColor')};
  }

  svg path {
    fill: ${props => (props.$active ? 'var(--ringil3-brand-color)' : 'currentColor')};
  }
`;

export default Tabs;